import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import CloseIcon from '../../../../components/icons/CloseIcon'
import EditIcon from '../../../../components/icons/EditIcon'
import useCommodityCodeSearch, { CombinedNomenclatureOption } from './useCommodityCodeSearch'
import { CommodityCodeOption, DIGIT_OR_WHITESPACE, mapToOptions } from './commodityCodeSearchOptions'
import { hasText } from '../../../../shared/lib/common-util'

interface ConsignmentItemCommodityCodeCellProps {
  isEditing: boolean
  isSaving: boolean
  editDisabled: boolean
  isEns: boolean
  country: string | null
  harmonizedSystemSubHeadingCode: string
  combinedNomenclatureCode: string
  onStartEditing: () => void
  onSelect: (harmonizedSystemSubHeadingCode: string, combinedNomenclatureCode: string) => void
  onCancel: () => void
}

function toSearchText(harmonizedSystemSubHeadingCode: string, combinedNomenclatureCode: string): string {
  const hsCode = harmonizedSystemSubHeadingCode.trim()
  const cnCode = combinedNomenclatureCode.trim()
  const hsCodeLabel = hsCode.length > 4 ? `${hsCode.substring(0, 4)} ${hsCode.substring(4, 6)}` : hsCode
  return `${hsCodeLabel} ${cnCode}`.trim()
}

function ConsignmentItemCommodityCodeCell({
  isEditing,
  isSaving,
  editDisabled,
  isEns,
  country,
  harmonizedSystemSubHeadingCode,
  combinedNomenclatureCode,
  onStartEditing,
  onSelect,
  onCancel,
}: ConsignmentItemCommodityCodeCellProps) {
  const { t } = useTranslation()

  const [currentSearch, setCurrentSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(0)
  const [options, setOptions] = useState<CombinedNomenclatureOption[]>([])
  const [wasPreviousPageLast, setWasPreviousPageLast] = useState(false)

  const { isLoading, data } = useCommodityCodeSearch(currentSearch, pageNumber, country)

  useEffect(() => {
    if (!isEditing) {
      setCurrentSearch('')
      setOptions([])
      setPageNumber(0)
      setWasPreviousPageLast(false)
      return
    }
    if (hasText(harmonizedSystemSubHeadingCode) || hasText(combinedNomenclatureCode)) {
      setCurrentSearch(toSearchText(harmonizedSystemSubHeadingCode, combinedNomenclatureCode))
    }
  }, [isEditing, harmonizedSystemSubHeadingCode, combinedNomenclatureCode])

  useEffect(() => {
    if (!data) return
    const newOptions = mapToOptions(data.content, country, isEns)
    setOptions((prevOptions) => (pageNumber !== 0 ? [...prevOptions, ...newOptions] : newOptions))
    setWasPreviousPageLast(data.last)
  }, [country, data, isEns, pageNumber])

  useEffect(() => {
    setPageNumber(0)
    setWasPreviousPageLast(false)
  }, [currentSearch])

  const selectOptions = useMemo<CommodityCodeOption[]>(
    () => options
      .filter((option) => !option.isArchived)
      .map((option) => ({
        value: option.value,
        label: `${option.codeLabel} - ${option.name}`,
        codeLabel: option.codeLabel,
        isArchived: option.isArchived,
      })),
    [options],
  )

  if (isEditing) {
    return (
      <div className="inline-edit commodity-code-cell">
        <Select<CommodityCodeOption, false>
          autoFocus
          openMenuOnFocus
          menuPlacement="auto"
          menuPosition="fixed"
          controlShouldRenderValue={false}
          className="select menu-list--auto trader-search commodity-code-cell__select"
          classNamePrefix="select"
          options={selectOptions}
          filterOption={null}
          isLoading={isLoading}
          isDisabled={isSaving}
          placeholder={t('declaration.p5.commodityCodeSearch')}
          noOptionsMessage={() => t('declaration.p5.commodityCodeSearch')}
          loadingMessage={() => t('common.loading')}
          inputValue={currentSearch}
          value={null}
          onInputChange={(newValue, actionMeta) => {
            if (actionMeta.action !== 'input-change') return
            if (!DIGIT_OR_WHITESPACE.test(newValue)) return
            setCurrentSearch(newValue)
            if (newValue === '') {
              setOptions([])
            }
          }}
          onChange={(option) => {
            if (!option) return
            const code = option.value
            if (code.length > 6) {
              onSelect(code.substring(0, 6), code.substring(6))
            } else {
              onSelect(code, '')
            }
          }}
          onMenuScrollToBottom={() => {
            if (!isLoading && !wasPreviousPageLast) {
              setPageNumber((page) => page + 1)
            }
          }}
        />
        <div
          className="inline-edit__controls"
          onClick={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Button
            type="button"
            variant="link"
            className="btn-icon"
            onClick={onCancel}
            disabled={isSaving}
          >
            <CloseIcon />
          </Button>
        </div>
      </div>
    )
  }

  const combinedCode = `${harmonizedSystemSubHeadingCode}${combinedNomenclatureCode}`

  if (editDisabled) {
    return <div className="inline-edit inline-edit--display">{combinedCode}</div>
  }

  return (
    <button
      type="button"
      className="inline-edit inline-edit--display inline-edit__trigger"
      onClick={(event) => {
        event.stopPropagation()
        onStartEditing()
      }}
      aria-label={`${t('declaration.p5.commodityCode')}: edit`}
    >
      <span className="truncate-inner">{combinedCode}</span>
      <EditIcon className="inline-edit__pencil" />
    </button>
  )
}

export default ConsignmentItemCommodityCodeCell
